import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Search, ExternalLink } from "lucide-react";

export default function COAHub() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/products").then(async (r) => {
      const products = r.data || [];
      const all = [];
      for (const p of products) {
        if (!p.has_active_coa) continue;
        try {
          const cr = await api.get("/coa/verify", { params: { sku: p.sku } });
          cr.data.forEach((c) => all.push({ ...c, product_image: p.image_url, product_tagline: p.tagline }));
        } catch {}
      }
      setItems(all);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const filtered = q
    ? items.filter((c) => `${c.batch_number} ${c.product?.product_name} ${c.product?.sku}`.toLowerCase().includes(q.toLowerCase()))
    : items;

  return (
    <div>
      <section className="bg-[var(--paper-2)] border-b border-[var(--line)] relative overflow-hidden">
        <div className="absolute inset-0 starfield opacity-50" />
        <div className="nebula-blob cyan absolute" style={{ width: 400, height: 400, top: '-40%', right: '5%', opacity: 0.15 }} />
        <div className="nebula-blob magenta absolute" style={{ width: 360, height: 360, bottom: '-50%', left: '15%', opacity: 0.12 }} />
        <div className="relative max-w-screen-2xl mx-auto px-4 sm:px-6 py-16">
          <div className="font-mono text-[11px] tracking-[0.22em] uppercase text-[var(--terra)]">Public verification portal</div>
          <h1 className="h-display text-4xl md:text-6xl mt-3 leading-[1.04]">COA <span className="gradient-text">Hub.</span></h1>
          <p className="mt-4 max-w-2xl text-[var(--ink-soft)]">Every active batch is independently identity-tested and published here. Search by batch number, SKU, or compound name.</p>
          <div className="mt-8 max-w-xl relative">
            <Search className="h-5 w-5 absolute left-4 top-1/2 -translate-y-1/2 text-[var(--terra)]" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Batch (NA26020A) or compound (BPC-157)…"
              className="w-full pl-12 pr-4 py-3.5 bg-[var(--paper-3)] text-[var(--ink)] font-mono text-sm rounded-full outline-none border border-[var(--line)] focus:border-[var(--terra)]"
              data-testid="coa-hub-search"
            />
          </div>
        </div>
      </section>

      <section className="max-w-screen-2xl mx-auto px-4 sm:px-6 py-12">
        {loading ? (
          <div className="font-mono text-xs uppercase tracking-[0.2em] text-[var(--ink-muted)]">Loading lab reports…</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.map((c) => (
              <div key={c.batch_number} className="glow-card overflow-hidden" data-testid={`coa-hub-tile-${c.batch_number}`}>
                <div className="flex">
                  <div className="w-32 h-32 bg-[var(--paper-3)] shrink-0">
                    {c.product_image && <img src={c.product_image} alt={c.product?.product_name} className="w-full h-full object-cover opacity-90" />}
                  </div>
                  <div className="flex-1 p-4">
                    <div className="font-mono text-[10.5px] tracking-[0.18em] uppercase text-[var(--ink-muted)]">{c.product?.sku}</div>
                    <div className="font-display text-lg font-medium leading-tight mt-1 text-[var(--ink)]">{c.product?.product_name}</div>
                    <div className="mt-2 font-mono text-xs text-[var(--ink-soft)]">
                      <span className="font-semibold text-[var(--terra)]">{c.purity_percent}%</span> purity · {c.testing_lab}
                    </div>
                  </div>
                </div>
                <div className="px-4 py-3 border-t border-[var(--line)] flex items-center justify-between bg-[var(--paper-3)]">
                  <div>
                    <div className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-[var(--ink-muted)]">Batch</div>
                    <div className="font-mono text-sm font-semibold text-[var(--terra)]">{c.batch_number}</div>
                  </div>
                  <Link to={`/verify?batch=${c.batch_number}`} className="btn-pill" data-testid={`view-coa-${c.batch_number}`}>
                    View report <ExternalLink className="inline h-3 w-3 ml-1" />
                  </Link>
                </div>
              </div>
            ))}
            {filtered.length === 0 && <div className="col-span-full text-center py-12 text-[var(--ink-muted)] font-mono text-xs uppercase tracking-[0.2em]">No matches.</div>}
          </div>
        )}
        <p className="mt-8 text-xs font-mono text-[var(--ink-muted)] max-w-2xl">For research use only. Lab reports document tested batch identity and purity; they are not medical or human-use guidance.</p>
      </section>
    </div>
  );
}
